import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HeroMiddleImage from '@assets/LandingPage/HeroMiddle.jpg';
import './HeroSection.css';

type SparklineMeta = {
  path: string;
  lastPoint: { x: number; y: number };
};

const buildSparkline = (
  values: number[],
  width = 200,
  height = 60
): SparklineMeta => {
  if (!values.length) {
    return { path: '', lastPoint: { x: 0, y: 0 } };
  }

  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const step = width / Math.max(values.length - 1, 1);

  const points = values.map((val, index) => {
    const x = index * step;
    const normalized = (val - min) / range;
    const y = height - normalized * height;
    return { x, y };
  });

  const controlPoint = (
    current: { x: number; y: number },
    previous: { x: number; y: number },
    next: { x: number; y: number },
    reverse = false
  ) => {
    const smooth = 0.25;
    const p = previous || current;
    const n = next || current;
    const angle = Math.atan2(n.y - p.y, n.x - p.x) + (reverse ? Math.PI : 0);
    const length = Math.sqrt((n.x - p.x) ** 2 + (n.y - p.y) ** 2) * smooth;
    return {
      x: current.x + Math.cos(angle) * length,
      y: current.y + Math.sin(angle) * length
    };
  };

  const bezierPath = points.reduce((acc, point, idx, arr) => {
    if (idx === 0) {
      return `M${point.x},${point.y}`;
    }
    const prev = arr[idx - 1];
    const next = arr[idx + 1];
    const cp1 = controlPoint(prev, arr[idx - 2], point);
    const cp2 = controlPoint(point, prev, next, true);
    return `${acc} C${cp1.x},${cp1.y} ${cp2.x},${cp2.y} ${point.x},${point.y}`;
  }, '');

  return {
    path: bezierPath,
    lastPoint: points[points.length - 1]
  };
};

const expenseBreakdown = {
  title: 'Low Training Costs',
  filterLabel: 'Lowest Expense',
  segments: [
    { label: 'Training', value: '€ 1,140', color: '#C6C1FF' },
    { label: 'Other', value: '€ 12,400', color: '#E0DBA2' }
  ]
};

const learningProgress = {
  title: 'Faster Learning Curve',
  growth: '+200%',
  timeline: ['Day 1', 'Day 2', 'Day 3'],
  data: [15, 18, 14, 22, 20, 28, 26]
};

const AssaHero = () => {
  const navigate = useNavigate();
  const [hasAnimated, setHasAnimated] = useState(false);
  const metricsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!metricsRef.current || hasAnimated) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setHasAnimated(true);
            if (metricsRef.current) {
              observer.unobserve(metricsRef.current);
            }
          }
        });
      },
      {
        threshold: 0.3,
        rootMargin: '0px 0px -15% 0px'
      }
    );

    observer.observe(metricsRef.current);

    return () => {
      if (metricsRef.current) {
        observer.unobserve(metricsRef.current);
      }
    };
  }, [hasAnimated]);

  const sparkline = buildSparkline(learningProgress.data);

  return (
    <div className="hero-section">
    <section className="hero-frame">
      <div className="hero-inner">
        <div className="ambient-glow"></div>
        <div className="hero">
          {/* LEFT – CONTENT */}
          <div className="content">
            <div className="badge">Enterprise learning platform</div>
            <h1>
              <span className="word" style={{animationDelay: '0.15s'}}>Your</span>{' '}
              <span className="word" style={{animationDelay: '0.22s'}}>team</span>{' '}
              <span className="word" style={{animationDelay: '0.29s'}}>is</span>{' '}
              <span className="word" style={{animationDelay: '0.36s'}}>being</span>{' '}
              <span className="word" style={{animationDelay: '0.43s'}}>trained.</span>{' '}
              <br />
              <span className="word" style={{animationDelay: '0.58s'}}>But</span>{' '}
              <span className="word" style={{animationDelay: '0.65s'}}>do</span>{' '}
              <span className="word" style={{animationDelay: '0.72s'}}>you</span>{' '}
              <span className="word" style={{animationDelay: '0.79s'}}>know</span>{' '}
              <span className="word" style={{animationDelay: '0.86s'}}>if</span>{' '}
              <span className="word" style={{animationDelay: '0.93s'}}>they're</span>{' '}
              <span className="accent">
                <span className="word" style={{animationDelay: '1.05s'}}>actually</span>{' '}
                <span className="word" style={{animationDelay: '1.18s'}}>learning?</span>
              </span>
            </h1>
            <p className="lede">Magnetix gives L&amp;D, HR, and onboarding teams complete visibility into who learned what — connecting every training hour to a real business outcome.</p>
            <div className="cta-row">
              <button className="btn-primary" onClick={() => navigate('/contact')}>Book a demo →</button>
            </div>
          </div>

          {/* RIGHT – SINGLE HERO IMAGE WITH FLOATING CARDS */}
          <div className="assa-hero__boxes-wrapper">
            <div className="assa-hero__boxes-desktop-layout">
              {/* Single Hero Image */}
              <img 
                src={HeroMiddleImage} 
                alt="Hero" 
                className="assa-hero__image"
              />

              {/* Floating Cards */}
              <div className="assa-hero__right-wrapper" ref={metricsRef}>
                {/* Upper Insight Card */}
                <div className="assa-hero__box assa-hero__box--right-upper">
                  <div className="assa-hero__insight-card">
                    <div className="assa-hero__insight-header">
                      <div>
                        <p className="assa-hero__insight-title">{expenseBreakdown.title}</p>
                      </div>
                      <button className="assa-hero__insight-filter">
                        {expenseBreakdown.filterLabel}
                      </button>
                    </div>
                    <div className="assa-hero__insight-body">
                      <div className="assa-hero__donut-chart">
                        <div className="assa-hero__donut-inner"></div>
                      </div>
                      <div className="assa-hero__legend">
                        {expenseBreakdown.segments.map((segment) => (
                          <div key={segment.label} className="assa-hero__legend-row">
                            <span
                              className="assa-hero__legend-dot"
                              style={{ backgroundColor: segment.color }}
                            ></span>
                            <span className="assa-hero__legend-label">{segment.label}</span>
                            <span className="assa-hero__legend-value">{segment.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Lower Progress Card */}
                <div className="assa-hero__box assa-hero__box--right-lower">
                  <div className="assa-hero__progress-card">
                    <div className="assa-hero__progress-header">
                      <p className="assa-hero__progress-title">{learningProgress.title}</p>
                      <span className="assa-hero__progress-pill">{learningProgress.growth}</span>
                    </div>
                    <div className="assa-hero__progress-graph">
                      <svg
                        viewBox="0 0 200 60"
                        preserveAspectRatio="none"
                        role="img"
                        aria-label="Learning curve sparkline"
                      >
                        <path
                          d={sparkline.path}
                          className={
                            hasAnimated
                              ? 'assa-hero__progress-line assa-hero__progress-line--animated'
                              : 'assa-hero__progress-line'
                          }
                          pathLength={1}
                        />
                        <circle
                          cx={sparkline.lastPoint.x}
                          cy={sparkline.lastPoint.y}
                          r={4}
                          className={
                            hasAnimated
                              ? 'assa-hero__progress-marker-circle assa-hero__progress-marker-circle--animated'
                              : 'assa-hero__progress-marker-circle'
                          }
                        />
                      </svg>
                    </div>
                    <div className="assa-hero__progress-timeline">
                      {learningProgress.timeline.map((label) => (
                        <span key={label}>{label}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
    </div>
  );
};

export default AssaHero;
